import React from 'react';

import {
    DndContext,
    DragEndEvent,
    DragMoveEvent,
    DragOverlay,
    PointerSensor,
    pointerWithin,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Typography } from '@mui/material';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface TreeDndItem {
    id: string;
    name: string;
    parent: string;
    order?: number;
}

export type TreeDndDropPosition = 'before' | 'after' | 'into';

export interface TreeDndProps<T extends TreeDndItem> {
    items: T[];
    onMove: (activeId: string, overId: string, position: TreeDndDropPosition) => Promise<void>;
    canDropInto?: (item: T) => boolean;
    canBeDropTarget?: (item: T) => boolean;
    renderRowPrefix?: (item: T) => React.ReactNode;
    renderRowSuffix?: (item: T) => React.ReactNode;
    renderRowExtra?: (item: T) => React.ReactNode;
    onSelect?: (id: string) => void;
    selectedId?: string | null;
    highlightedIds?: Set<string>;
    onRowHover?: (id: string | null) => void;
}

// ─── Internal types ───────────────────────────────────────────────────────────

type FlatItem<T extends TreeDndItem> = T & {
    depth: number;
    parentId: string;
};

// ─── buildFlatList ────────────────────────────────────────────────────────────

function buildFlatList<T extends TreeDndItem>(items: T[]): FlatItem<T>[] {
    const result: FlatItem<T>[] = [];

    function traverse(parentId: string, depth: number) {
        const children = items.filter((z) => (z.parent || 'root') === parentId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        for (const item of children) {
            result.push({ ...item, depth, parentId });
            traverse(item.id, depth + 1);
        }
    }

    traverse('root', 0);
    return result;
}

// ─── TreeDndRow ───────────────────────────────────────────────────────────────

interface TreeDndRowProps<T extends TreeDndItem> {
    item: FlatItem<T>;
    dropPosition: TreeDndDropPosition | null;
    isDragActive: boolean;
    isSelected: boolean;
    isHighlighted: boolean;
    onSelect?: (id: string) => void;
    onRowHover?: (id: string | null) => void;
    renderRowPrefix?: (item: T) => React.ReactNode;
    renderRowSuffix?: (item: T) => React.ReactNode;
    renderRowExtra?: (item: T) => React.ReactNode;
}

function TreeDndRow<T extends TreeDndItem>({
    item,
    dropPosition,
    isDragActive,
    isSelected,
    isHighlighted,
    onSelect,
    onRowHover,
    renderRowPrefix,
    renderRowSuffix,
    renderRowExtra
}: TreeDndRowProps<T>) {
    const { attributes, listeners, setNodeRef: setDraggableRef, isDragging } = useDraggable({ id: item.id });
    const { setNodeRef: setDroppableRef } = useDroppable({ id: item.id });

    return (
        <Box sx={{ position: 'relative' }}>
            {dropPosition === 'before' && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        zIndex: 10,
                        pointerEvents: 'none'
                    }}
                />
            )}
            <Box
                ref={setDroppableRef}
                onMouseEnter={() => onRowHover?.(item.id)}
                onMouseLeave={() => onRowHover?.(null)}
                sx={{
                    borderRadius: 1,
                    opacity: isDragging ? 0.3 : 1,
                    '&:hover': isDragActive ? {} : { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                    ...(isSelected && !dropPosition && { bgcolor: 'action.selected' }),
                    ...(dropPosition === 'into' && {
                        bgcolor: 'action.selected',
                        outline: '2px solid',
                        outlineColor: 'primary.main'
                    })
                }}
            >
                <Box
                    data-automation="tree-dnd-row"
                    onClick={() => !isDragActive && onSelect?.(item.id)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: item.depth * 3,
                        py: 0.25,
                        cursor: !isDragActive && onSelect ? 'pointer' : 'default'
                    }}
                >
                    <Box ref={setDraggableRef} {...attributes} {...listeners} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.5, flexShrink: 0, cursor: 'grab' }} />
                        {renderRowPrefix && renderRowPrefix(item)}
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{ cursor: 'inherit', userSelect: 'none', display: 'block', py: 0.5, fontWeight: isHighlighted ? 'bold' : 'normal' }}
                        >
                            {item.name}
                        </Typography>
                    </Box>
                    {renderRowSuffix && (
                        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, ml: 1 }}>
                            {renderRowSuffix(item)}
                        </Box>
                    )}
                </Box>
                {renderRowExtra && renderRowExtra(item)}
            </Box>
            {dropPosition === 'after' && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        zIndex: 10,
                        pointerEvents: 'none'
                    }}
                />
            )}
        </Box>
    );
}

// ─── TreeDnd ──────────────────────────────────────────────────────────────────

function TreeDnd<T extends TreeDndItem>({
    items,
    onMove,
    canDropInto,
    canBeDropTarget,
    renderRowPrefix,
    renderRowSuffix,
    renderRowExtra,
    onSelect,
    selectedId,
    highlightedIds,
    onRowHover
}: TreeDndProps<T>) {
    const [activeId, setActiveId] = React.useState<string | null>(null);
    const [dropTarget, setDropTarget] = React.useState<{ id: string; position: TreeDndDropPosition } | null>(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const flatList = React.useMemo(() => buildFlatList(items), [items]);

    function isDescendant(potentialDescendantId: string, ancestorId: string): boolean {
        let currentId: string | undefined = potentialDescendantId;
        while (currentId) {
            const current = items.find((i) => i.id === currentId);
            if (!current) {
                return false;
            }
            const parentId = current.parent || 'root';
            if (parentId === ancestorId) {
                return true;
            }
            if (parentId === 'root') {
                return false;
            }
            currentId = parentId;
        }
        return false;
    }

    function handleDragMove(event: DragMoveEvent) {
        const { over, activatorEvent, delta, active } = event;
        if (!over || over.id === active.id) {
            setDropTarget(null);
            return;
        }

        const overId = String(over.id);
        const overItem = items.find((i) => i.id === overId);

        if (overItem && canBeDropTarget && !canBeDropTarget(overItem)) {
            setDropTarget(null);
            return;
        }

        const rect = over.rect;
        const initialY = (activatorEvent as PointerEvent).clientY;
        const currentY = initialY + delta.y;
        const relativeY = currentY - rect.top;
        const ratio = relativeY / rect.height;

        let position: TreeDndDropPosition;
        if (ratio < 0.3) {
            position = 'before';
        } else if (ratio > 0.7) {
            position = 'after';
        } else {
            const canInto = overItem !== undefined && !isDescendant(overId, String(active.id)) && (canDropInto ? canDropInto(overItem) : true);
            if (canInto) {
                position = 'into';
            } else {
                position = ratio < 0.5 ? 'before' : 'after';
            }
        }

        setDropTarget({ id: overId, position });
    }

    async function handleDragEnd(event: DragEndEvent) {
        const { active } = event;
        setActiveId(null);
        const current = dropTarget;
        setDropTarget(null);

        if (!current || current.id === String(active.id)) {
            return;
        }

        await onMove(String(active.id), current.id, current.position);
    }

    function handleDragCancel() {
        setActiveId(null);
        setDropTarget(null);
    }

    const activeItem = activeId ? flatList.find((i) => i.id === activeId) : null;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={(e) => setActiveId(String(e.active.id))}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            {flatList.map((item) => (
                <TreeDndRow<T>
                    key={item.id}
                    item={item}
                    dropPosition={dropTarget?.id === item.id ? dropTarget.position : null}
                    isDragActive={activeId !== null}
                    isSelected={selectedId === item.id}
                    isHighlighted={highlightedIds?.has(item.id) ?? false}
                    onSelect={onSelect}
                    onRowHover={onRowHover}
                    renderRowPrefix={renderRowPrefix}
                    renderRowSuffix={renderRowSuffix}
                    renderRowExtra={renderRowExtra}
                />
            ))}
            <DragOverlay dropAnimation={null}>
                {activeItem && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            pl: activeItem.depth * 3,
                            py: 0.25,
                            bgcolor: 'background.paper',
                            boxShadow: 3,
                            borderRadius: 1,
                            opacity: 0.9
                        }}
                    >
                        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.5 }} />
                        <Typography variant="body2">{activeItem.name}</Typography>
                    </Box>
                )}
            </DragOverlay>
        </DndContext>
    );
}

export { TreeDnd };
